import { Card, Flex, Text } from "@jsq/ds/core";
import {
  FormSelect,
  FormTextInput,
  FormRadioButton,
  FormRadioGroup,
  useFormContext,
  FormTextarea,
  CancelButton,
  SubmitButton,
} from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { Profile } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructionChange/PositionsTable/types";
import CountryFormField from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/CountryFormField";
import RiskRatingRadioBar from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/RiskRatingRadioBar";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { shouldShowRiskRating } from "~/pages/Compliance/utils";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { SPACING } from "~/styles/variables";
import history from "~/utils/history";
import { ComplianceCaseProfileType } from "../../../types";
import { FormValues, InvestingProfile, Option } from "../../CreateBankAccountAction/types";
import { cardContainer, createProfileStyle } from "../styles_vanilla.css";
import { prepareProfileOptions } from "../utils";

interface Props {
  investingProfile?: ProfileNameFields | null;
  profiles?: ComplianceCaseProfileType[] | Profile[] | undefined;
  onExit: () => void;
  submitButtonText?: string;
  showingInModal?: boolean;
  label: string;
  caseId: number;
  programId?: number;
  positionId?: string;
  createdProfile?: Profile | null;
}

const CreateBankAccountInformationCard = ({
  investingProfile,
  profiles,
  onExit,
  submitButtonText = "Save",
  showingInModal,
  label,
  caseId,
  programId,
  positionId,
  createdProfile,
}: Props) => {
  const { values } = useFormContext<FormValues>();
  const showRiskRating = shouldShowRiskRating(programId);

  const investingProfileName = showingInModal
    ? (investingProfile as Profile)?.legalName ||
      `${(investingProfile as Profile)?.firstName || ""} ${(investingProfile as Profile)?.lastName || ""}`.trim() ||
      "--"
    : getInvestingProfileName(investingProfile);

  const investingProfileId = showingInModal
    ? (investingProfile as Profile)?.id
    : (investingProfile as InvestingProfile)?.id;

  const investingProfileOption: Option[] =
    investingProfile && investingProfileId !== undefined
      ? [
          {
            id: investingProfileId,
            name: investingProfileName,
          },
        ]
      : [];

  const createdProfileOption: Option | null =
    createdProfile && typeof createdProfile.id === "number"
      ? {
          id: createdProfile.id,
          name: `${createdProfile.legalName || ""} ${createdProfile.firstName || ""} ${createdProfile.lastName || ""}`.trim(),
        }
      : null;

  const profileOptions = prepareProfileOptions(profiles, showingInModal);

  const handleCreateNewProfileClick = () => {
    history.push(
      `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers${positionId ? `?positionId=${positionId}` : ""}`,
    );
  };

  const handleEditNewProfileClick = () => {
    history.push(
      `${globalEnv.complianceRoute}/case/${caseId}/edit-profile${
        positionId
          ? `?${[
              positionId ? `positionId=${positionId}` : null,
              `profileId=${createdProfile?.id ?? ""}`,
              programId ? `programId=${programId}` : null,
            ]
              .filter(Boolean)
              .join("&")}`
          : ""
      }`,
    );
  };

  return (
    <Card
      title={showingInModal ? "" : "Bank account information"}
      className={showingInModal ? cardContainer : undefined}
    >
      <FormSelect
        name="accountHolderId"
        label={label}
        full
        style={{ marginBottom: SPACING.MEDIUM }}
        data-testid="accountHolderId"
        placeholder={showingInModal ? "Select profile" : ""}
        handleChange={(value: string | number | string[] | number[]) => {
          if (value === "create_new_profile") {
            handleCreateNewProfileClick();
          } else if (value === "edit_new_profile") {
            handleEditNewProfileClick();
          }
        }}
      >
        {
          [
            <FormSelect.Section
              key="profile"
              name={showingInModal ? "Profiles" : "[INVESTMENT ACCOUNT NAME]"}
            >
              {profileOptions.map((option: Option) => (
                <FormSelect.Option key={option.name} value={option.id}>
                  {option.name}
                </FormSelect.Option>
              ))}
            </FormSelect.Section>,
            <FormSelect.Section key="investment" name="Investment account">
              {investingProfileOption.map((option: Option) => (
                <FormSelect.Option key={option.name} value={option.id}>
                  {option.name}
                </FormSelect.Option>
              ))}
            </FormSelect.Section>,
            createdProfileOption && (
              <FormSelect.Section key="newProfile" name="New profile">
                <FormSelect.Option
                  key={createdProfileOption.name}
                  value={createdProfileOption.id}
                >
                  {createdProfileOption.name}
                </FormSelect.Option>
              </FormSelect.Section>
            ),
            showingInModal &&
              (!createdProfile ? (
                <FormSelect.Section key="create">
                  <FormSelect.Option value="create_new_profile" className={createProfileStyle}>
                    + Create a new profile
                  </FormSelect.Option>
                </FormSelect.Section>
              ) : (
                <FormSelect.Section key="edit">
                  <FormSelect.Option value="edit_new_profile" className={createProfileStyle}>
                    Edit new profile
                  </FormSelect.Option>
                </FormSelect.Section>
              )),
          ].filter(Boolean) as React.ReactElement[]
        }
      </FormSelect>{" "}
      {values.accountHolderId === 0 && (
        <Flex
          style={{
            padding: SPACING.MEDIUM,
            backgroundColor: Colors.Informative200,
            marginBottom: SPACING.MEDIUM,
          }}
        >
          <Text variant="strong-md" style={{ color: Colors.Neutral700 }}>
            Please make sure to include this bank account owner in watchlist screening. If the
            screening returns a positive result, create a profile for the bank account owner and
            perform KYC on them.
          </Text>
        </Flex>
      )}
      {values.showBankAndCountry && (
        <Flex style={{ marginBottom: SPACING.MEDIUM }} gap={20}>
          <Flex flexGrow={1} alignContent="flex-start">
            <FormTextInput name="bankName" label="Bank name" isFullWidth />
          </Flex>
          <Flex flexGrow={1} alignContent="flex-end">
            <CountryFormField />
          </Flex>
        </Flex>
      )}
      <FormRadioGroup style={{ marginBottom: SPACING.MEDIUM }} name="accountNameMatches">
        <FormRadioButton value="yes">
          Account (investor, owner, or controller) exactly matches bank account name
        </FormRadioButton>
        <FormRadioButton value="no">
          Account (investor, owner, or controller) does not exactly match bank account name
        </FormRadioButton>
      </FormRadioGroup>
      {values.accountNameMatches === "no" && (
        <Flex flexGrow={1} style={{ marginBottom: SPACING.MEDIUM }}>
          <FormTextInput name="bankAccountName" label="Bank account name" isFullWidth />
        </Flex>
      )}
      {values.accountNameMatches === "no" && (
        <Flex flexGrow={1} style={{ marginBottom: SPACING.MEDIUM }} flexDirection="column">
          <FormTextarea
            name="explanation"
            label="Explanation"
            placeholder="Please explain how this bank account owner is related to the investor"
            isFullWidth
          />
        </Flex>
      )}
      {showRiskRating && <RiskRatingRadioBar form="bankAccount" />}
      <Flex justifyContent="flex-end" style={{ marginTop: SPACING.LARGE }}>
        <CancelButton onClick={onExit} css={{ marginRight: SPACING.MEDIUM }} />
        <SubmitButton>{submitButtonText}</SubmitButton>
      </Flex>
    </Card>
  );
};

export default CreateBankAccountInformationCard;
